import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { loadProfile, updateProfile, changePassword } from "../api/profileApi";
import { SERVER_URL, AUTH_STORAGE_KEY } from "../socket";
import { resolveAttachmentHref } from "../utils/mailUtils";

export default function useProfile(auth, authHeaders) {
  const [profileDetails, setProfileDetails] = useState({ fullName: "", mobileNumber: "", profileImageUrl: "" });
  const [profileForm, setProfileForm] = useState({ fullName: "", mobileNumber: "", profileImageUrl: "" });
  const [profileMessage, setProfileMessage] = useState("");
  const [profileMessageType, setProfileMessageType] = useState("info");
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileUploading, setProfileUploading] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState("");
  const profileLoadedRef = useRef(false);

  const [changePasswordForm, setChangePasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [changePasswordMsg, setChangePasswordMsg] = useState("");
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);

  const initialProfile = useMemo(() => ({
    fullName: auth?.user?.fullName || "",
    mobileNumber: auth?.user?.mobileNumber || "",
    profileImageUrl: auth?.user?.profileImageUrl || "",
  }), [auth?.user?.fullName, auth?.user?.mobileNumber, auth?.user?.profileImageUrl]);

  useEffect(() => {
    setProfileDetails(initialProfile);
    setProfileForm(initialProfile);
    setProfileImagePreview(initialProfile.profileImageUrl ? resolveAttachmentHref(SERVER_URL, initialProfile.profileImageUrl) : "");
  }, [initialProfile]);

  const updateStoredAuthUser = useCallback((mutator) => {
    try {
      const raw = localStorage.getItem(AUTH_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;
      if (!parsed.user || typeof parsed.user !== "object") return;

      // Only update stored auth when it belongs to the same user as `auth.user`
      const storedEmail = parsed.user.email || parsed.user.username || "";
      const currentEmail = auth?.user?.email || auth?.user?.username || "";
      if (!storedEmail || !currentEmail || String(storedEmail).toLowerCase() !== String(currentEmail).toLowerCase()) {
        return;
      }

      const currentUser = parsed.user;
      const nextUser = typeof mutator === "function" ? mutator({ ...currentUser }) : { ...currentUser, ...(mutator || {}) };
      if (!nextUser || typeof nextUser !== "object") return;

      parsed.user = nextUser;
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(parsed));
    } catch {}
  }, [auth]);

  const refreshProfile = useCallback(async () => {
    if (!authHeaders) return;
    setProfileMessage("");
    setProfileMessageType("info");

    try {
      const payload = await loadProfile(authHeaders);
      setProfileDetails(payload);
      setProfileForm(payload);
      setProfileImagePreview(payload.profileImageUrl ? resolveAttachmentHref(SERVER_URL, payload.profileImageUrl) : "");
      updateStoredAuthUser((current) => ({ ...current, ...payload }));
      profileLoadedRef.current = true;
    } catch (err) {
      setProfileMessageType("error");
      setProfileMessage(err.response?.data?.error || "Failed to load profile");
    }
  }, [authHeaders, updateStoredAuthUser]);

  useEffect(() => {
    profileLoadedRef.current = false;
  }, [authHeaders]);

  const ensureProfileLoaded = useCallback(async () => {
    if (profileLoadedRef.current) return;
    await refreshProfile();
  }, [refreshProfile]);

  const handleProfileInputChange = useCallback((event) => {
    const { name, value } = event.target || {};
    if (!name) return;
    if (name === "mobileNumber") {
      const digitsOnly = String(value || "").replace(/\D+/g, "").slice(0, 10);
      setProfileForm((prev) => ({ ...prev, mobileNumber: digitsOnly }));
      return;
    }
    setProfileForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleProfileImageRemove = useCallback(() => {
    setProfileForm((prev) => ({ ...prev, profileImageUrl: "" }));
    setProfileImagePreview("");
    setProfileMessageType("info");
    setProfileMessage("Profile image removed. Save to apply changes.");
  }, []);

  const handleProfileImageSelect = useCallback(async (event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    if (!authHeaders) {
      setProfileMessageType("error");
      setProfileMessage("Authentication required to upload profile image");
      if (event.target) event.target.value = "";
      return;
    }

    setProfileUploading(true);
    setProfileMessage("");
    setProfileMessageType("info");

    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${SERVER_URL}/api/attachmentsprofile`, formData, {
        headers: authHeaders,
      });
      const uploadedUrl = res.data?.url;
      if (!uploadedUrl) {
        setProfileMessageType("error");
        setProfileMessage("Failed to upload image. Try again.");
      } else {
        setProfileForm((prev) => ({ ...prev, profileImageUrl: uploadedUrl }));
        setProfileImagePreview(resolveAttachmentHref(SERVER_URL, uploadedUrl));
        setProfileMessageType("success");
        setProfileMessage("Profile image uploaded. Save to confirm.");
      }
    } catch (err) {
      const message = err.response?.data?.error || "Failed to upload image";
      setProfileMessageType("error");
      setProfileMessage(message);
    } finally {
      setProfileUploading(false);
      if (event.target) event.target.value = "";
    }
  }, [authHeaders]);

  const handleProfileSubmit = useCallback(async (event) => {
    event?.preventDefault?.();
    if (!authHeaders) {
      setProfileMessageType("error");
      setProfileMessage("Authentication required to update profile");
      return;
    }

    const fullName = (profileForm.fullName || "").trim();
    const mobileNumber = (profileForm.mobileNumber || "");
    const profileImageUrl = (profileForm.profileImageUrl || "");

    if (!fullName) {
      setProfileMessageType("error");
      setProfileMessage("Full name cannot be empty");
      return;
    }

    if (mobileNumber && !/^[0-9]{10}$/.test(mobileNumber)) {
      setProfileMessageType("error");
      setProfileMessage("Enter a valid 10-digit mobile number");
      return;
    }

    const changes = {};
    if (fullName !== profileDetails.fullName) {
      changes.fullName = fullName;
    }
    if (mobileNumber !== profileDetails.mobileNumber) {
      changes.mobileNumber = mobileNumber;
    }
    if (profileImageUrl !== profileDetails.profileImageUrl) {
      changes.profileImageUrl = profileImageUrl;
    }

    if (!Object.keys(changes).length) {
      setProfileMessageType("info");
      setProfileMessage("No profile changes to save.");
      return;
    }

    try {
      setProfileSaving(true);
      setProfileMessage("");
      setProfileMessageType("info");
      const data = await updateProfile(changes, authHeaders);
      if (data) {
        const nextProfile = {
  fullName: fullName,
  mobileNumber: mobileNumber, // 👈 use plain input value
  profileImageUrl: profileImageUrl,
};
        setProfileDetails(nextProfile);
        setProfileForm(nextProfile);
        setProfileImagePreview(nextProfile.profileImageUrl ? resolveAttachmentHref(SERVER_URL, nextProfile.profileImageUrl) : "");
        updateStoredAuthUser((current) => ({
          ...current,
          fullName: nextProfile.fullName,
          mobileNumber: nextProfile.mobileNumber,
          profileImageUrl: nextProfile.profileImageUrl,
        }));
        profileLoadedRef.current = true;
      }
      setProfileMessageType("success");
      setProfileMessage("Profile updated successfully");
    } catch (err) {
      const message = err.response?.data?.error || "Failed to update profile";
      setProfileMessageType("error");
      setProfileMessage(message);
    } finally {
      setProfileSaving(false);
    }
  }, [authHeaders, profileDetails, profileForm, updateStoredAuthUser]);

  const handleChangePasswordInput = useCallback((event) => {
    const { name, value } = event.target || {};
    if (!name) return;
    setChangePasswordForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChangePasswordSubmit = useCallback(async (event) => {
    event?.preventDefault?.();
    if (changePasswordLoading) return;

    const currentPassword = changePasswordForm.currentPassword.trim();
    const newPassword = changePasswordForm.newPassword.trim();
    const confirmPassword = changePasswordForm.confirmPassword.trim();

    if (!currentPassword || !newPassword || !confirmPassword) {
      setChangePasswordMsg("Fill in all password fields");
      return;
    }

    if (newPassword.length < 6) {
      setChangePasswordMsg("New password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setChangePasswordMsg("New passwords do not match");
      return;
    }

    if (!authHeaders) {
      setChangePasswordMsg("Authentication expired. Sign in again.");
      return;
    }

    try {
      setChangePasswordLoading(true);
      setChangePasswordMsg("");
      await changePassword({ currentPassword, newPassword }, authHeaders);
      setChangePasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setChangePasswordMsg("Password updated successfully");
    } catch (err) {
      const message = err.response?.data?.error || "Failed to change password";
      setChangePasswordMsg(message);
    } finally {
      setChangePasswordLoading(false);
    }
  }, [authHeaders, changePasswordForm, changePasswordLoading]);

  return {
    profileDetails,
    profileForm,
    profileMessage,
    profileMessageType,
    profileSaving,
    profileUploading,
    profileImagePreview,
    refreshProfile,
    ensureProfileLoaded,
    setProfileForm,
    handleProfileInputChange,
    handleProfileSubmit,
    handleProfileImageSelect,
    handleProfileImageRemove,
    changePasswordForm,
    changePasswordMsg,
    changePasswordLoading,
    handleChangePasswordInput,
    handleChangePasswordSubmit,
    setChangePasswordMsg,
  };
}
